import * as React from 'react'

import { DataTableFilter, DataTableSource } from 'generated/resources/datatable.graphql'
import once from 'lodash/once'

import useFiltersReducer from 'modules/core/components/DataTable/hooks/useFiltersReducer'
import type { Filter } from 'modules/core/components/DataTable/types'

import { PaginationStateContext } from './PaginationStateContext'

type Context<U> = {
  filters: Array<Filter<U>>
  availableFilters: Array<Filter<U>>
  onAddFilter: (filter: Filter<U>) => void
  onRemoveFilter: (filter: Filter<U>) => void
  onUpdateFilter: (filter: Filter<U>) => void
  onResetFilters: () => void
  getFiltersVariables: () => FilterVariable
  enableClearFilters?: boolean
}

type Props<T, U, S extends string> = {
  persistFilters: boolean
  enableClearFilters?: boolean
  children: React.ReactNode
  source: DataTableSource<T, S>
  filters?: Array<DataTableFilter<U>>
}

type FilterVariable = Record<string, Record<string | number | symbol, unknown>>

function createContext<U>() {
  return React.createContext<Context<U> | null>(null)
}

export const createFiltersContext = once(createContext)

export function FiltersProvider<T, U, S extends string>({
  source,
  children,
  filters: filtersProp,
  persistFilters,
  enableClearFilters,
}: Props<T, U, S>) {
  const Context = createFiltersContext<U>()
  const paginationVariables = React.useContext(PaginationStateContext)

  if (!paginationVariables) {
    throw new Error('PaginationStateContext is not defined')
  }

  const { resetPagination } = paginationVariables

  const onChangeFilter = (filter?: Filter<U>, value?: any) => {
    resetPagination()

    if (filter && filter?.value !== undefined) {
      filter.filter.onUpdateValue?.(filter)
      return
    }
    if (filter && (value === null || value === undefined)) {
      filter.filter.onUpdateValue?.(undefined)
    } else if (!filter) {
      filters?.forEach((f) => f.filter.onUpdateValue?.(undefined))
    }
  }

  const { filters, availableFilters, onAddFilter, onRemoveFilter, onUpdateFilter, onResetFilters } =
    useFiltersReducer<T, U, S>({
      source,
      onChangeFilterCallback: onChangeFilter,
      incomingFilters: filtersProp,
      persistFilters,
    })

  const getFiltersVariables = () => {
    const filterVariable: FilterVariable = {}

    filters.forEach((filter) => {
      const { value, filter: filterInfo } = filter

      if (
        value === undefined ||
        (Array.isArray(value) && value.every((v) => v === null || v === undefined))
      )
        return

      const operation = filter.category.operation

      if (!operation) {
        throw new Error(`Filter ${filter.filter.identifier} has no operation`)
      }

      const filterPath = filterInfo.path.toString()
      const isEntityFilter = filterInfo.type === 'entity' && Array.isArray(value)

      filterVariable[filterPath] = {
        [operation]: isEntityFilter ? value.map((entity) => entity.id) : value,
      }
    })

    return { filter: filterVariable }
  }

  const value = {
    filters,
    availableFilters,
    onAddFilter,
    onRemoveFilter,
    onUpdateFilter,
    onResetFilters,
    getFiltersVariables,
    enableClearFilters,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}
